import React, { useCallback, useEffect, useState } from 'react';
import {
  Grid,
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography
} from '@mui/material';
import TagsInput from '../components/TagsInput';
import { useValuePackForm } from 'hooks/useValuePackForm';
import { BASIC_INFO_FIELDS } from 'config/valuePackFormConfig';
import { STATUS_OPTIONS } from 'config/valuePackFormConfig';
import { FormSwitchField, FormTextField } from '../components/FormField';
import moment from 'moment';

// Simplified Image Upload Component
const SimpleImageUpload = ({ 
  title, 
  onFileSelect, 
  previewUrl, 
  error, 
  helperText,
  required = false 
}) => {
  const [localPreview, setLocalPreview] = useState('');

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Create preview URL
      const preview = URL.createObjectURL(file);
      setLocalPreview(preview);
      
      // Pass file to parent
      onFileSelect(file);
    }
  };

  const displayPreview = previewUrl || localPreview;

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardHeader 
        title={
          <Box display="flex" alignItems="center" gap={0.5}>
            {title}
            {required && (
              <Typography component="span" color="error" variant="body1" lineHeight={1}>*</Typography>
            )}
          </Box>
        }
        subheader={
          <Typography variant="caption" color={required ? 'error' : 'text.secondary'}>
            {required ? 'Required' : 'Optional'}
          </Typography>
        }
      />
      <CardContent>
        {/* Preview */}
        {displayPreview && (
          <Box mb={2} textAlign="center">
            <img 
              src={displayPreview} 
              alt="Preview" 
              style={{
                width: '100%',
                maxHeight: '200px',
                objectFit: 'cover',
                borderRadius: '8px'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </Box>
        )}
        
        {/* File Input */}
        <Box mt={2}>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ width: '100%' }}
          />
        </Box>

        {/* Error Display */}
        {error && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {helperText}
          </Typography>
        )}

        {!error && helperText && (
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            {helperText}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const BasicInfoStep = ({ stepIndex = 0, isEdit, validationTriggered = false }) => {
  const { 
    control, 
    getFieldValue, 
    setFieldValue, 
    hasFieldError, 
    getFieldError,
    watch,
    reduxEditMode,
    trigger
  } = useValuePackForm(stepIndex, isEdit, validationTriggered);

  const publishedAt = watch('publishedAt');
  const expiresAt = watch('expiresAt');

  // Handle tags
  const handleAddTag = useCallback((tag) => {
    const currentTags = getFieldValue('tags') || [];
    const newTags = [...currentTags, tag];
    setFieldValue('tags', newTags);
    
    if (validationTriggered) {
      trigger('tags');
    }
  }, [getFieldValue, setFieldValue, validationTriggered, trigger]);

  const handleRemoveTag = useCallback((index) => {
    const currentTags = getFieldValue('tags') || [];
    const newTags = currentTags.filter((_, i) => i !== index);
    setFieldValue('tags', newTags);
    
    if (validationTriggered) {
      trigger('tags');
    }
  }, [getFieldValue, setFieldValue, validationTriggered, trigger]);

  const tags = getFieldValue('tags') || [];
  
  // Simplified image handling - sirf file store karein
  const handleImageUpload = useCallback((fieldName, file) => {
    console.log("🟢 Image selected:", { fieldName, file });
    
    // Direct file store karein - consistent field names use karein
    if (fieldName === 'mainImage') {
      setFieldValue('imageFile', file);
    } else if (fieldName === 'bannerImage') {
      setFieldValue('bannerImageFile', file);
    }
    
    // Validate if needed
    if (validationTriggered) {
      trigger(fieldName === 'mainImage' ? 'imageUrl' : 'bannerImageUrl');
    }
  }, [setFieldValue, validationTriggered, trigger]);

  // Get preview URLs
  const mainImageFile = getFieldValue('imageFile');
  const bannerImageFile = getFieldValue('bannerImageFile');
  
  const mainImagePreview = mainImageFile ? URL.createObjectURL(mainImageFile) : getFieldValue('imageUrl') || '';
  const bannerImagePreview = bannerImageFile ? URL.createObjectURL(bannerImageFile) : getFieldValue('bannerImageUrl') || '';

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    return moment(dateString).format('YYYY-MM-DDTHH:mm');
  };

  const handleFieldBlur = useCallback((fieldName) => {
    if (validationTriggered) {
      trigger(fieldName);
    }
  }, [validationTriggered, trigger]);

  return (
    <Grid container spacing={3}>
      {/* Main Form Fields */}
      <Grid item xs={12} md={8}>
        {Object.entries(BASIC_INFO_FIELDS).filter(([key]) => 
          ['name', 'subTitle', 'slug', 'shortDescription', 'description'].includes(key)
        ).map(([fieldName, fieldConfig]) => (
          <FormTextField
            key={fieldName}
            name={fieldName}
            control={control}
            label={fieldConfig.label}
            placeholder={fieldConfig.placeholder}
            helperText={fieldConfig.helperText}
            multiline={fieldConfig.multiline}
            rows={fieldConfig.rows}
            maxLength={fieldConfig.maxLength}
            required={true}
            onBlur={() => handleFieldBlur(fieldName)}
          />
        ))}

        {/* Tags Input */}
        <Box mt={2}>
          <TagsInput
            tags={tags}
            onAddTag={handleAddTag}
            onRemoveTag={handleRemoveTag}
            error={hasFieldError('tags')}
            helperText={getFieldError('tags') || 'Add relevant tags for categorization'}
            required={true}
          />
        </Box>

      </Grid>
          
      {/* Sidebar - Simplified Image Upload */}
      <Grid item xs={12} md={4}>
        <SimpleImageUpload
          title="Main Image"
          onFileSelect={(file) => handleImageUpload('mainImage', file)}
          previewUrl={mainImagePreview}
          error={hasFieldError('imageUrl')}
          helperText={getFieldError('imageUrl') || 
            (reduxEditMode ? 
              'Upload main image (optional for updates)' : 
              'Upload main image (required)'
            )
          }
          required={!reduxEditMode}
        />

        <SimpleImageUpload
          title="Banner Image"
          onFileSelect={(file) => handleImageUpload('bannerImage', file)}
          previewUrl={bannerImagePreview}
          error={hasFieldError('bannerImageUrl')}
          helperText={getFieldError('bannerImageUrl') || 'Upload banner image (required)'}
          required={true}
        />

        {/* Status Settings Card */}
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardHeader title="Status Settings" />
          <CardContent>
            {STATUS_OPTIONS.map((option) => (
              <Box key={option.key} mb={2}>
                <FormSwitchField
                  name={option.key}
                  control={control}
                  label={option.label}
                  description={option.description}
                  color={option.color}
                />
              </Box>
            ))}

            {/* Date Fields */}
            <Box mt={3}>
              <FormTextField
                name="publishedAt"
                control={control}
                label="Publish Date"
                type="datetime-local"
                helperText={reduxEditMode ? 
                  "When this value pack should become available (past dates allowed for updates)" : 
                  "When this value pack should become available (must be future date)"}
                InputLabelProps={{ shrink: true }}
                value={formatDateForInput(publishedAt)}
                required={true}
                onBlur={() => handleFieldBlur('publishedAt')}
              />
              
              <FormTextField
                name="expiresAt"
                control={control}
                label="Expiry Date"
                type="datetime-local"
                helperText={reduxEditMode ?
                  "When this value pack should expire (past dates allowed for updates)" :
                  "When this value pack should expire (must be future date and after publish date)"}
                InputLabelProps={{ shrink: true }}
                value={formatDateForInput(expiresAt)}
                required={true}
                onBlur={() => handleFieldBlur('expiresAt')}
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default React.memo(BasicInfoStep);
